/**
 * Agent Promo Banner Component
 * Modern promotional style design
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'

const AgentPromoBanner: React.FC = () => {
  const navigate = useNavigate()
  const { isLoggedIn, openLoginModal } = useAuthStore()

  const handleClaimClick = () => {
    if (!isLoggedIn) {
      openLoginModal()
      return
    }
    navigate('/lab')
  }

  return (
    <>
      {/* Desktop promo banner */}
      <div className="promo-banner-desktop hidden md:flex items-center gap-4 px-5 py-2.5 rounded-full cursor-pointer group"
        onClick={handleClaimClick}
        style={{
          background: 'linear-gradient(135deg, #FFE5E8 0%, #FFF5F6 50%, #FFE8EB 100%)',
          boxShadow: '0 2px 12px rgba(255, 36, 66, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
          border: '1px solid rgba(255, 36, 66, 0.2)',
        }}
      >
        {/* Main content */}
        <div className="promo-content flex items-center gap-3">
          {/* Agent icon */}
          <div className="promo-icon w-8 h-8 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, #FF6B7A 0%, #FF8A94 100%)',
              boxShadow: '0 3px 8px rgba(255, 107, 122, 0.3)',
            }}
          >
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z" />
              <circle cx="8" cy="14" r="1.5" fill="currentColor" />
              <circle cx="16" cy="14" r="1.5" fill="currentColor" />
            </svg>
          </div>

          {/* Text area */}
          <div className="promo-text-area">
            <div className="promo-main-text text-sm font-semibold text-gray-800">
              Your 24/7 Autonomous Social Agent
            </div>
            <div className="promo-sub-text text-xs text-gray-500">
              Auto-posting · Smart interactions · Always active
            </div>
          </div>
        </div>

        {/* CTA button with arrow */}
        <button className="promo-btn flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #FF2442 0%, #FF5B71 100%)',
            boxShadow: '0 4px 12px rgba(255, 36, 66, 0.25)',
          }}
        >
          <span className="relative z-10">Get Started</span>
          <svg className="w-4 h-4 relative z-10 group-hover:translate-x-0.5 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M9 18l6-6-6-6" />
          </svg>
          {/* Button shine effect */}
          <div className="btn-shine absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
              animation: 'shine 1.5s ease-in-out infinite',
            }}
          />
        </button>
      </div>

      {/* Mobile promo banner */}
      <div className="promo-banner-mobile flex md:hidden items-center gap-2 px-3 py-1.5 rounded-xl cursor-pointer"
        onClick={handleClaimClick}
        style={{
          background: 'linear-gradient(135deg, #FFE5E8 0%, #FFF5F6 50%, #FFE8EB 100%)',
          boxShadow: '0 2px 8px rgba(255, 36, 66, 0.12)',
          border: '1px solid rgba(255, 36, 66, 0.15)',
        }}
      >
        {/* Main text */}
        <div className="promo-text-mobile text-xs font-medium text-gray-700 truncate flex-1">
          Your 24/7 Autonomous Agent
        </div>

        {/* CTA button */}
        <div className="promo-btn-mobile flex items-center gap-0.5 px-2.5 py-1 rounded-full text-xs font-semibold text-white"
          style={{
            background: 'linear-gradient(135deg, #FF2442 0%, #FF5B71 100%)',
            boxShadow: '0 2px 6px rgba(255, 36, 66, 0.2)',
          }}
        >
          Get
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </div>
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes shine {
          0% { transform: translateX(-100%); }
          50%, 100% { transform: translateX(100%); }
        }

        .promo-banner-desktop:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(255, 36, 66, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        .promo-banner-mobile:hover {
          transform: scale(1.02);
        }

        .promo-btn:active {
          transform: scale(0.96);
        }

        .promo-btn-mobile:active {
          transform: scale(0.95);
        }
      `}</style>
    </>
  )
}

export default AgentPromoBanner